import { useEffect, useState } from 'react';

import { clsx } from 'clsx';
import ReactModal from 'react-modal';

import { Failed } from './Failed/Failed';
import { Loading } from './Loading/Loading';
import { Success } from './Success/Success';
import {
  useGetBuyerSpendingLimit,
  usePostFundSeller,
} from '../../../../api/lending/lapiHooks';
import { useAccountNavigation } from '../../../../hooks/useAccountNavigation';
import { useIsOverlay } from '../../../hooks/useIsOverlay';
import { useModal } from '../../../store/ModalProvider';
import { buildQueryString } from '../../../utils/queryParams';
import { useCurrentFormalOfferDuration } from '../../hooks/useCurrentFormalOfferDuration';

export const DRAW_DOWN_MODAL = 'DrawDownModal';

export const DISPLAY_TYPE_ENUM = {
  DEFAULT: 'DEFAULT',
  ECOMMERCE: 'ECOMMERCE',
  OVERLAY: 'OVERLAY',
} as const;
export type TDisplayType =
  (typeof DISPLAY_TYPE_ENUM)[keyof typeof DISPLAY_TYPE_ENUM];

export const DrawDownModal = () => {
  const { navigateToAccount } = useAccountNavigation();
  const { isOpen } = useModal(DRAW_DOWN_MODAL);
  const { postFundSeller, isLoading, isSuccess, isError } = usePostFundSeller();
  const { spendingLimit } = useGetBuyerSpendingLimit();
  const { currentFormalOfferDuration } = useCurrentFormalOfferDuration();

  // We can't use active spending limit as we are getting their spending limits before funding
  // the seller (as active spending limit doesn't update fast enough after funding). So their active
  // spending limit may be a different duration to the one they're using this time.
  const currentSpendingLimit = spendingLimit?.data.spending_limits.find(
    (spending_limit) =>
      spending_limit?.duration_in_months === currentFormalOfferDuration,
  );

  const [redirectUrl, setRedirectUrl] = useState('');

  const isOverlay = useIsOverlay();

  const getDisplayType = () => {
    if (isOverlay) return DISPLAY_TYPE_ENUM.OVERLAY;
    if (redirectUrl) return DISPLAY_TYPE_ENUM.ECOMMERCE;

    return DISPLAY_TYPE_ENUM.DEFAULT;
  };

  const displayType = getDisplayType();

  const finishDrawDown = () => {
    navigateToAccount(buildQueryString({ postCheckout: 'true' }));
  };

  useEffect(() => {
    async function handlePostFundSeller() {
      if (!isOpen) return;

      try {
        const { redirect_url } = await postFundSeller();

        if (redirect_url) {
          setRedirectUrl(redirect_url);
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        // Ignore the error because we're checking it via isError
      }
    }

    void handlePostFundSeller();
  }, [isOpen, postFundSeller, isOverlay]);

  const showOverlay = isLoading || isSuccess || isError;

  return (
    <>
      {showOverlay && <div className="fixed inset-0 bg-modal-overlay" />}
      <DrawDownReactModal isOpen={isLoading} fadeOut>
        <Loading />
      </DrawDownReactModal>

      <DrawDownReactModal isOpen={isSuccess} fadeIn>
        <Success
          finishDrawDown={finishDrawDown}
          displayType={displayType}
          redirectUrl={redirectUrl}
          spendingLimit={currentSpendingLimit}
        />
      </DrawDownReactModal>

      <DrawDownReactModal isOpen={isError} fadeIn>
        <Failed finishDrawDown={finishDrawDown} />
      </DrawDownReactModal>
    </>
  );
};

const DrawDownReactModal = ({
  isOpen,
  children,
  fadeIn = false,
  fadeOut = false,
}: {
  isOpen: boolean;
  children: React.ReactNode;
  fadeIn?: boolean;
  fadeOut?: boolean;
}) => {
  return (
    <ReactModal
      className={{
        base: clsx(
          'flex h-max min-h-[366px] w-[640px] items-center rounded-l bg-white p-xl outline-none transition-all duration-200 ease-in',
          { 'draw-down-modal-base': fadeIn },
        ),
        afterOpen: clsx({ 'draw-down-modal-after-open': fadeIn }),
        beforeClose: clsx({ 'draw-down-modal-before-close': fadeOut }),
      }}
      overlayClassName="fixed inset-0 flex items-center justify-center"
      isOpen={isOpen}
      shouldCloseOnOverlayClick={false}
      ariaHideApp={false}
      closeTimeoutMS={fadeOut ? 200 : 0}
    >
      <div className="mx-auto flex h-full flex-col bg-white text-center">
        {children}
      </div>
    </ReactModal>
  );
};
